<template>
  <b-modal
    :id="modalId"
    header-class="header-qr-custom"
    body-class="body-qr-custom"
    content-class="content-qr-custom"
    hide-footer
    :title="$t('otherInfo')"
  >
    <template v-if="meeting">
      <!-- QR Code Centered Box with Download icon next to it -->
      <div class="qr-container-row">
        <!-- Left spacer of 48px to balance the download button + gap and keep the QR card perfectly centered -->
        <div class="qr-spacer"></div>
        <div class="qr-card">
          <qr-code ref="qrContent" class="qr-code-element" :size="qrsize" :text="displayParticipantJoinUrl"></qr-code>
        </div>
        <button class="btn-qr-download" @click="downloadQR()" v-b-tooltip.hover :title="$t('download')">
          <b-icon-download />
        </button>
      </div>

      <!-- Meeting Code and Security Code Side-by-Side -->
      <div class="qr-info-grid">
        <div v-if="meeting.joinCode" class="info-card">
          <div class="info-card-header">
            <b-icon-key class="mr-1" style="transform: rotate(-45deg);" />
            <span>{{ $t('meetingCode') }}</span>
          </div>
          <div class="info-card-body">
            <span class="info-card-value">{{ meeting.joinCode }}</span>
            <b-button class="btn-copy-mini" @click="copyText(meeting.joinCode)" v-b-tooltip.hover :title="$t('copy')">
              <b-icon-files />
            </b-button>
          </div>
        </div>

        <div v-if="meeting.accessCode" class="info-card">
          <div class="info-card-header">
            <b-icon-shield-lock class="mr-1" />
            <span>{{ $t('accessCode') }}</span>
          </div>
          <div class="info-card-body">
            <span class="info-card-value">{{ meeting.accessCode }}</span>
            <b-button class="btn-copy-mini" @click="copyText(meeting.accessCode)" v-b-tooltip.hover :title="$t('copy')">
              <b-icon-files />
            </b-button>
          </div>
        </div>
      </div>

      <!-- Join link (truncated URL with copy button) -->
      <div v-if="displayParticipantJoinUrl" class="link-info-card">
        <div class="link-info-content">
          <div class="link-info-header">
            <b-icon-people class="mr-1" />
            <span>{{ $t('joinLink9DigitsLabel') }}</span>
          </div>
          <a :href="appGmUrl" target="_blank" class="link-info-value" v-b-tooltip.hover :title="appGmUrl">{{ appGmUrl }}</a>
        </div>
        <b-button class="btn-copy-modern" @click="copyText(appGmUrl)" v-b-tooltip.hover :title="$t('copy')">
          <b-icon-files />
        </b-button>
      </div>

      <!-- Host Reopen link with adminCode -->
      <div v-if="meeting.adminCode && hostJoinUrlWithAdminCode" class="link-info-card reopen-card mt-2">
        <div class="d-flex align-items-center justify-content-between w-100" style="gap: 12px;">
          <div class="link-info-content">
            <div class="link-info-header reopen-header">
              <svg class="mr-1 flex-shrink-0" width="12" height="12" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14.6167 3.94994H8.74999C8.1842 3.94994 7.64157 4.17471 7.2415 4.57478C6.84142 4.97486 6.61666 5.51748 6.61666 6.08328V8.74994M11.4167 0.75L14.6167 3.94994L11.4167 7.15M12.4833 10.3501V15.6834C12.4833 15.9663 12.371 16.2376 12.1709 16.4377C11.9709 16.6377 11.6996 16.7501 11.4167 16.7501H1.81667C1.53377 16.7501 1.26246 16.6377 1.06242 16.4377C0.862381 16.2376 0.75 15.9663 0.75 15.6834V7.15009C0.75 6.8672 0.862381 6.59588 1.06242 6.39585C1.26246 6.19581 1.53377 6.08343 1.81667 6.08343H3.41667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>{{ $t('reopenMeetingLink') }}</span>
            </div>
            <a :href="hostJoinUrlWithAdminCode" target="_blank" class="link-info-value" v-b-tooltip.hover :title="hostJoinUrlWithAdminCode">{{ hostJoinUrlWithAdminCode }}</a>
          </div>
          <b-button class="btn-copy-modern" @click="copyText(hostJoinUrlWithAdminCode)" v-b-tooltip.hover :title="$t('copy')">
            <b-icon-files />
          </b-button>
        </div>

        <div class="reopen-hint-section">
          <div class="hint-item">
            <b-icon-lightbulb class="hint-icon" />
            <span>{{ $t('reopenMeetingHint') }}</span>
          </div>
          <div class="warning-item">
            <b-icon-shield-exclamation class="warning-icon" />
            <span>{{ $t('reopenMeetingSecurityWarning') }}</span>
          </div>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script lang="ts">
import { Component, Prop, Ref } from "vue-property-decorator";
import BaseComponent from "@/components/BaseComponent";

@Component
export default class MeetingOtherInfoModal extends BaseComponent {
  @Prop({ default: "qr-code-modal" }) modalId!: string;
  @Prop({ required: true }) meeting!: any;
  @Prop({ default: "" }) participantJoinUrl!: string;
  @Prop({ default: 500 }) qrsize!: number;

  @Ref("qrContent") qrContent!: any;

  get displayParticipantJoinUrl(): string {
    if (this.participantJoinUrl) return this.participantJoinUrl;
    if (this.meeting?.participantJoinUrl) {
      const url = this.meeting.participantJoinUrl;
      return url.startsWith("http") ? url : this.formatUrl(url);
    }
    return "";
  }

  get appGmUrl(): string {
    return window.location.origin + "/app/#/gm";
  }

  get hostJoinUrlWithAdminCode(): string {
    if (!this.meeting || !this.meeting.adminCode) {
      return "";
    }
    const adminCode = this.meeting.adminCode.toString().trim();
    if (!adminCode) {
      return "";
    }
    let hostUrl = this.meeting.hostJoinUrl;
    if (!hostUrl) {
      const secretKey = this.meeting.meetingSecretKey || this.meeting.secretKey;
      if (secretKey) {
        hostUrl = window.location.origin + "/app/#/host/" + secretKey;
      }
    }
    if (!hostUrl) return "";
    if (!hostUrl.startsWith("http")) {
      hostUrl = this.formatUrl(hostUrl);
    }
    // Đảm bảo param accessCode luôn lấy chính là adminCode
    if (/([?&])accessCode=[^&]*/.test(hostUrl)) {
      hostUrl = hostUrl.replace(/([?&])accessCode=[^&]*/, `$1accessCode=${encodeURIComponent(adminCode)}`);
    } else if (/([?&])adminCode=[^&]*/.test(hostUrl)) {
      hostUrl = hostUrl.replace(/([?&])adminCode=[^&]*/, `$1accessCode=${encodeURIComponent(adminCode)}`);
    } else {
      const separator = hostUrl.includes("?") ? "&" : "?";
      hostUrl = `${hostUrl}${separator}accessCode=${encodeURIComponent(adminCode)}`;
    }
    return hostUrl;
  }

  downloadQR() {
    if (this.qrContent) {
      const url = this.qrContent.$el?.lastChild?.src;
      if (!url) return;
      const a = document.createElement("a");
      a.href = url;
      a.download = "QRcode_" + (this.meeting?.name || "meeting");
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  }

  async copyText(text: string) {
    if (!text) return;
    await navigator.clipboard.writeText(text);
    this.toastSuccessDelay(this.$t("copied").toString());
  }
}
</script>

<style lang="scss" scoped>
.qr-container-row {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  position: relative;
  margin-bottom: 24px;
}

.qr-spacer {
  width: 48px;
  flex-shrink: 0;
}

.qr-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  padding: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.02);
  display: flex;
  justify-content: center;
  align-items: center;
}

.qr-code-element {
  width: 220px;
  height: 220px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.qr-code-element ::v-deep img,
.qr-code-element ::v-deep canvas {
  max-width: 100% !important;
  max-height: 100% !important;
  display: block !important;
}

.btn-qr-download {
  margin-left: 12px;
  background: rgba(255, 255, 255, 0.75) !important;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(229, 231, 235, 0.9) !important;
  border-radius: 8px !important;
  width: 36px;
  height: 36px;
  display: flex !important;
  align-items: center;
  justify-content: center;
  color: #4b5563 !important;
  cursor: pointer;
  transition: all 0.2s ease-in-out !important;
  padding: 0 !important;
  margin-bottom: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05) !important;
  flex-shrink: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.95) !important;
    color: #1f2937 !important;
    transform: translateY(-1px);
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08) !important;
  }

  &:focus {
    outline: none;
  }
}

.qr-info-grid {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
}

.info-card {
  flex: 1;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative;
}

.info-card-header {
  display: flex;
  align-items: center;
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  margin-bottom: 4px;
  letter-spacing: 0.5px;

  .b-icon {
    font-size: 12px;
    color: #4b5563;
  }
}

.info-card-body {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.info-card-value {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  word-break: break-all;
}

.btn-copy-mini {
  border: none !important;
  background-color: transparent !important;
  color: #64748b !important;
  padding: 0 !important;
  width: 24px;
  height: 24px;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  border-radius: 6px !important;
  transition: all 0.15s ease !important;

  &:hover {
    background-color: #f1f5f9 !important;
    color: #0f172a !important;
  }
}

.btn-copy-modern {
  border: 1px solid #cbd5e1 !important;
  background-color: #ffffff !important;
  color: #475569 !important;
  border-radius: 8px !important;
  width: 32px;
  height: 32px;
  display: flex !important;
  align-items: center;
  justify-content: center;
  padding: 0 !important;
  transition: all 0.2s ease !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05) !important;
  flex-shrink: 0;

  &:hover {
    background-color: #2f55a4 !important;
    border-color: #2f55a4 !important;
    color: #ffffff !important;
  }

  &:focus {
    box-shadow: 0 0 0 2px rgba(47, 85, 164, 0.2) !important;
  }
}

.link-info-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  transition: all 0.2s ease-in-out;

  &:hover {
    background-color: #f1f5f9;
    border-color: #cbd5e1;
  }
}

.link-info-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
}

.link-info-header {
  display: flex;
  align-items: center;
  font-size: 11px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  .b-icon {
    font-size: 13px;
    color: #2563eb;
  }
}

.link-info-card.reopen-card {
  background: #f0f7ff;
  border-color: #bfdbfe;
  flex-direction: column;
  align-items: stretch;

  &:hover {
    background-color: #e6f0fa;
    border-color: #93c5fd;
  }

  .reopen-header {
    color: #1e40af;

    svg,
    .b-icon {
      color: #2563eb;
      font-size: 13px;
    }
  }

  .reopen-hint-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
    background-color: #ffffff;
    border: 1px solid #dbeafe;
    border-radius: 8px;
    padding: 10px 12px;
    margin-top: 6px;
    font-size: 12px;

    .hint-item {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      color: #475569;
      line-height: 1.45;

      .hint-icon {
        font-size: 14px;
        color: #d97706;
        flex-shrink: 0;
        margin-top: 1px;
      }
    }

    .warning-item {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      color: #991b1b;
      font-weight: 500;
      line-height: 1.45;
      background-color: #fef2f2;
      border: 1px solid #fee2e2;
      border-radius: 6px;
      padding: 6px 10px;

      .warning-icon {
        font-size: 14px;
        color: #dc2626;
        flex-shrink: 0;
        margin-top: 1px;
      }
    }
  }
}

.link-info-value {
  align-self: flex-start;
  width: fit-content;
  max-width: 100%;
  font-size: 13px;
  color: #1d4ed8;
  text-decoration: none;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color 0.2s ease;

  &:hover {
    color: #1e40af;
    text-decoration: underline;
  }
}

::v-deep .content-qr-custom {
  border-radius: 20px !important;
  background-color: #f4f6fa !important;
  border: none !important;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08) !important;
}

::v-deep .header-qr-custom {
  border-bottom: none !important;
  padding: 20px 24px 8px 24px !important;
  background-color: transparent !important;

  .modal-title {
    font-size: 20px !important;
    font-weight: 600 !important;
    color: #111827 !important;
  }

  .close {
    font-size: 24px !important;
    color: #9ca3af !important;
    opacity: 1 !important;
    transition: color 0.2s ease !important;
    outline: none !important;

    &:hover {
      color: #374151 !important;
    }
  }
}

::v-deep .body-qr-custom {
  padding: 8px 24px 24px 24px !important;
  background-color: transparent !important;
}
</style>
